import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Loader2, MessageCircle, Send, Twitter } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { isStandaloneApp } from "../utils/pwa";
import ShareButton from "./ShareButton";
import Turnstile, { turnstileActive } from "./Turnstile";
import { useHoneypot } from "./Honeypot";

export default function Footer() {
  const { t, lang, company } = useLanguage();
  const [nlEmail, setNlEmail] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [nlDone, setNlDone] = useState(false);
  const [nlCaptcha, setNlCaptcha] = useState(null);
  const [svc, setSvc] = useState(null);
  const hp = useHoneypot();

  // Badge stato sistemi in tempo reale: check all'ingresso e ogni 2 minuti
  useEffect(() => {
    let alive = true;
    const check = () => api.get("/status").then(({ data }) => { if (alive) setSvc(data.data); }).catch(() => { if (alive) setSvc({ status: "outage" }); });
    check();
    const iv = setInterval(check, 120000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (nlBusy || !nlEmail.includes("@")) return;
    if (turnstileActive() && !nlCaptcha) {
      toast.error(t("misc.captcha"));
      return;
    }
    setNlBusy(true);
    try {
      await api.post("/newsletter", { email: nlEmail, lang, turnstile_token: nlCaptcha, ...hp.payload() });
      setNlDone(true);
      toast.success(t("news.subscribed"));
    } catch (e2) {
      const detail = e2.response?.data?.detail || "";
      toast.error(t(detail === "invalid_email" ? "news.invalid" : detail.startsWith("security_check") ? "misc.captcha" : "misc.error"));
    } finally {
      setNlBusy(false);
    }
  };

  const cols = [
    {
      title: t("footer.explore"),
      // nella PWA installata (modalità app) il blog resta sul web: niente link qui
      links: [
        [t("nav.destinations"), "/destinations"],
        [t("nav.howItWorks"), "/how-it-works"],
        [t("footer.business"), "/business"],
        ...(!isStandaloneApp() ? [[t("footer.blog"), "/blog"], [t("footer.app"), "/app"]] : []),
        [t("compat.nav"), "/compatibility"],
        [t("acc.topup"), "/topup"],
        [t("nav.myEsims"), "/account"],
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        [t("legal.terms"), "/legal/terms"],
        [t("legal.privacy"), "/legal/privacy"],
        [t("legal.cookies"), "/legal/cookies"],
        [t("legal.refunds"), "/legal/refunds"],
      ],
    },
    {
      title: t("footer.support"),
      links: [
        [t("chat.title"), "#support"],
        [t("footer.charter"), "/carta-servizi"],
        [t("footer.cookiePrefs"), "#cookies"],
        ["info@vediloo.it", "mailto:info@vediloo.it"],
        ["+39 06 84102697", "tel:+390684102697"],
      ],
    },
  ];

  // In modalità app (PWA installata): footer ridotto all'essenziale — logo, supporto, copyright
  if (isStandaloneApp()) {
    return (
      <footer data-testid="footer" className="bg-[var(--bg-dark)] text-white">
        <div className="px-4 max-w-7xl mx-auto py-7 flex flex-col items-center gap-3.5 text-center">
          <div className="font-display font-extrabold text-xl tracking-tight">
            SIMAMMA<span className="text-[var(--accent-terracotta)]">.</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-300">
            <button data-testid="footer-support-chat" onClick={() => window.dispatchEvent(new CustomEvent("atlas-open-chat"))}
              className="inline-flex items-center gap-1.5 hover:text-[var(--accent-terracotta)] transition-colors">
              <MessageCircle className="w-3.5 h-3.5" /> {t("chat.title")}
            </button>
            <a href="mailto:info@vediloo.it" data-testid="footer-support-email"
              className="hover:text-[var(--accent-terracotta)] transition-colors">info@vediloo.it</a>
          </div>
          <div className="font-mono-t text-[9px] uppercase tracking-[0.25em] text-slate-500">
            © {new Date().getFullYear()} SIMAMMA · {company?.name || "EI S.r.l."} — P.IVA IT{company?.vat || "16017671005"}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer data-testid="footer" className="bg-[var(--bg-dark)] text-white">
      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14">
        <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
          <div>
            <div className="font-display font-extrabold text-3xl tracking-tight">
              SIMAMMA<span className="text-[var(--accent-terracotta)]">.</span>
            </div>
            <p className="text-sm text-slate-400 mt-3 max-w-xs leading-relaxed">{t("footer.tag")}</p>
            <div className="flex items-center gap-3 mt-5" data-testid="footer-socials">
              {[["Facebook", "https://facebook.com/vediloo", Facebook],
                ["Instagram", "https://instagram.com/vediloo", Instagram],
                ["X", "https://twitter.com/vediloo", Twitter]].map(([label, href, Icon]) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  data-testid={`footer-social-${String(label).toLowerCase()}`}
                  className="w-9 h-9 rounded-full border border-[var(--border-dark)] flex items-center justify-center text-slate-400 hover:text-[var(--accent-terracotta)] hover:border-[var(--accent-terracotta)] transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <div className="mt-6 max-w-xs">
              <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">{t("news.title")}</div>
              {nlDone ? (
                <p data-testid="newsletter-done" className="text-sm text-emerald-400">{t("news.subscribed")}</p>
              ) : (
                <div>
                  <form data-testid="newsletter-form" onSubmit={subscribe} className="flex rounded-full border border-[var(--border-dark)] overflow-hidden focus-within:border-[var(--accent-terracotta)] transition-colors">
                    <hp.Field />
                    <input data-testid="newsletter-input" type="email" required value={nlEmail}
                      onChange={(e) => setNlEmail(e.target.value)} placeholder={t("news.placeholder")}
                      className="flex-1 min-w-0 bg-transparent px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none" />
                    <button data-testid="newsletter-submit" type="submit" disabled={nlBusy}
                      className="px-4 text-[var(--accent-terracotta)] hover:text-white transition-colors disabled:opacity-50">
                      {nlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </form>
                  <div className="mt-3">
                    <Turnstile onToken={setNlCaptcha} />
                  </div>
                </div>
              )}
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-4">{c.title}</div>
              <ul className="space-y-2.5">
                {c.links.map(([label, href]) => (
                  <li key={href}>
                    {href === "#support" || href === "#cookies" ? (
                      <button
                        data-testid={href === "#support" ? "footer-support-chat" : "footer-cookie-prefs"}
                        onClick={() => window.dispatchEvent(new CustomEvent(href === "#support" ? "atlas-open-chat" : "atlas-open-cookies"))}
                        className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[var(--accent-terracotta)] transition-colors"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> {label}
                      </button>
                    ) : href.startsWith("mailto:") || href.startsWith("tel:") ? (
                      <a href={href} data-testid="footer-support-email" className="text-sm text-slate-300 hover:text-[var(--accent-terracotta)] transition-colors">{label}</a>
                    ) : (
                      <Link to={href} data-testid={`footer-link-${href.replace(/\//g, "-").slice(1)}`} className="text-sm text-slate-300 hover:text-[var(--accent-terracotta)] transition-colors">
                        {label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-[var(--border-dark)] mt-12 pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">
          <span>© {new Date().getFullYear()} SIMAMMA · {company?.name || "EI S.r.l."} — {company?.country || "Italia"} — P.IVA IT{company?.vat || "16017671005"}. {t("footer.rights")}</span>
          <span className="flex items-center gap-4">
            {svc && (
              <Link to="/carta-servizi" data-testid="service-status-badge"
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                <span className={`w-2 h-2 rounded-full shrink-0 ${
                  svc.status === "operational" ? "bg-emerald-400 animate-pulse"
                  : svc.status === "degraded" ? "bg-amber-400" : "bg-red-500"}`} />
                <span className={svc.status === "operational" ? "text-emerald-400" : svc.status === "degraded" ? "text-amber-400" : "text-red-400"}>
                  {t(svc.status === "operational" ? "status.ok" : svc.status === "degraded" ? "status.degraded" : "status.outage")}
                </span>
              </Link>
            )}
            <ShareButton testid="share-site" url="/" title="SIMAMMA" text={t("share.textSite")}
              className="!bg-transparent !border-[var(--border-dark)] !text-slate-400 hover:!border-[var(--accent-terracotta)] hover:!text-[var(--accent-terracotta)]" />
            <span data-testid="footer-powered">Powered by Airalo Partner API · Stripe</span>
          </span>
        </div>
      </div>
    </footer>
  );
}
